import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { rpc, table } from '@/lib/db';
import type { PermissionCode } from '@/lib/permissions';
import type { LanguageCode } from '@/types/domain';

export interface CurrentUser {
  id: string;
  fullName: string;
  email: string;
  position: string;
  orgUnitId: string | null;
  orgUnitName: string;
  roles: string[];
  permissions: string[];
  language: LanguageCode;
  avatarUrl: string | null;
}

interface LoginPayload {
  email: string;
  password: string;
  remember?: boolean;
}

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Пользователь вошёл в Supabase Auth, но не заведён в core.users. */
  isUnlinked: boolean;
  /** Ошибка загрузки профиля: недоступна схема, нет прав, сеть. */
  profileError: string | null;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface UserRow {
  id: string;
  full_name: string;
  email: string;
  position_title: string | null;
  org_unit_id: string | null;
  language: LanguageCode;
  avatar_path: string | null;
}

/**
 * Загружает профиль сотрудника по идентификатору из Supabase Auth.
 * Возвращает null, если запись в core.users ещё не создана: такой пользователь
 * прошёл аутентификацию, но не имеет ни роли, ни прав.
 */
async function loadProfile(authUserId: string): Promise<CurrentUser | null> {
  const { data, error } = await table('core', 'users')
    .select('id, full_name, email, position_title, org_unit_id, language, avatar_path')
    .eq('auth_user_id', authUserId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  const row = data as unknown as UserRow;

  // Права считает сама база — фронтенд лишь прячет недоступные разделы.
  const permissions = await rpc<string[] | null>('core', 'my_permissions');

  let orgUnitName = '';
  if (row.org_unit_id) {
    const { data: unit } = await table('core', 'org_units')
      .select('name')
      .eq('id', row.org_unit_id)
      .maybeSingle();
    orgUnitName = ((unit as { name?: string } | null)?.name ?? '') as string;
  }

  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    position: row.position_title ?? '',
    orgUnitId: row.org_unit_id,
    orgUnitName,
    roles: [],
    permissions: permissions ?? [],
    language: row.language,
    avatarUrl: row.avatar_path,
  };
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnlinked, setIsUnlinked] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const applySession = useCallback(async (authUserId: string | null) => {
    if (!authUserId) {
      setUser(null);
      setIsUnlinked(false);
      setProfileError(null);
      return;
    }
    try {
      const profile = await loadProfile(authUserId);
      setUser(profile);
      // Профиль отсутствует и запрос при этом отработал — учётка действительно не привязана
      setIsUnlinked(profile === null);
      setProfileError(null);
    } catch (error) {
      // Отличаем «нет записи в core.users» от «запрос вообще не прошёл»:
      // раньше обе ситуации показывались одинаково и уводили диагностику не туда
      const message = error instanceof Error ? error.message : String(error);
      console.error('Не удалось загрузить профиль пользователя', error);
      setUser(null);
      setIsUnlinked(false);
      setProfileError(
        message.includes('Invalid schema') || message.includes('PGRST106')
          ? 'Схемы БД не опубликованы в API. Settings → API → Exposed schemas: добавьте core, billing, infrastructure, dispatch, integration, reporting'
          : message,
      );
    }
  }, []);

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      await applySession(data.session?.user.id ?? null);
      if (!cancelled) setIsLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void applySession(session?.user.id ?? null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [applySession]);

  const login = useCallback(
    async ({ email, password }: LoginPayload) => {
      if (!supabase) throw new Error('Supabase не настроен');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
      await applySession(data.user?.id ?? null);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    await supabase?.auth.signOut();
    setUser(null);
    setIsUnlinked(false);
    setProfileError(null);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    if (!supabase) throw new Error('Supabase не настроен');
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw new Error(error.message);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      isUnlinked,
      profileError,
      login,
      logout,
      requestPasswordReset,
    }),
    [user, isLoading, isUnlinked, profileError, login, logout, requestPasswordReset],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth должен вызываться внутри AuthProvider');
  return context;
}

export interface PermissionsApi {
  can: (permission: PermissionCode | string) => boolean;
  canAny: (permissions: readonly (PermissionCode | string)[]) => boolean;
  canAll: (permissions: readonly (PermissionCode | string)[]) => boolean;
  permissions: string[];
  roles: string[];
}

export function usePermissions(): PermissionsApi {
  const { user } = useAuth();

  return useMemo<PermissionsApi>(() => {
    const granted = new Set(user?.permissions ?? []);
    const isSuperuser = granted.has('*');
    const can = (permission: PermissionCode | string) => isSuperuser || granted.has(permission);
    return {
      can,
      canAny: (list) => list.length === 0 || list.some(can),
      canAll: (list) => list.every(can),
      permissions: user?.permissions ?? [],
      roles: user?.roles ?? [],
    };
  }, [user]);
}
